/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

/**
 * This file contains all shared styles used across
 * different containers - the application container, events container,
 * students container, and access control container.
 */

import { Link } from "react-router-dom";

import styled from "styled-components";

import {
  getTheme,
  FontSizes,
  FontWeights,
  IButtonStyles,
  ICommandBarStyles,
  ISearchBoxStyles,
  IStackStyles,
  IStackTokens,
  ITextStyles,
} from "@fluentui/react";

import { FlexRow } from "../../Components/Common/Results/Result.styles";

const theme = getTheme();

/**
 * Styling for the table container area
 */
export const containerStackStyles = (): IStackStyles => {
  return {
    root: {
      height: "100%",
    },
  };
};

/**
 * Styling for the container stack
 */
export const containerStackTokens = (): IStackTokens => {
  return {
    childrenGap: 36,
  };
};

/**
 * Styling for the wrapper stack
 */
export const wrapperStackStyles = (): IStackStyles => {
  return {
    root: {
      padding: "0 3.25rem 0 1.25rem",
    },
  };
};

export const commandBarStyles: ICommandBarStyles = {
  root: {
    borderRadius: "8px",
    boxShadow: " 0px 1px 4px rgba(0, 0, 0, 0.2)",
    margin: "1rem 0.5rem 0",
    padding: "0.75rem",
    selectors: {
      ["button"]: {
        marginRight: "36px",
      },
      [".ms-Button-label"]: {
        fontSize: "14px",
        fontWeight: FontWeights.semibold,
      },
    },
  },
};

/**
 * Styling for the button stack.
 */
export const buttonStackTokens = (): IStackTokens => {
  return {
    childrenGap: 15,
  };
};

/**
 * Styling for filter button
 */
export const filterButtonStyles = (): IStackStyles => {
  return {
    root: {
      marginTop: "10px",
      display: "block",
    },
  };
};

/**
 * Styling for list stack (right side of container)
 */
// TODO: this is duplicated from students container, should fix that
export const listStackStyles = (): IStackStyles => {
  return {
    root: {
      backgroundColor: theme.palette.white,
      borderRadius: "0.5rem",
    },
  };
};

export const emptyStackStyles = (): IStackStyles => {
  return {
    root: {
      height: "36px",
    },
  };
};

/**
 * Aligned center flex row.
 */
export const FlexRowAlignedCenter = styled.div`
  display: flex;
  align-items: center;

  p {
    margin: 0;
    margin-right: 20px;
  }
`;

/**
 * Styling for the row that holds the title and results.
 */
export const PaddedFlexRow = styled(FlexRow)`
  padding: 23px;
  padding-bottom: 0; // no padding needed, margin is on <p /> tag.
`;

/**
 * Styling for the search box
 */
export const searchBoxStyles = (): ISearchBoxStyles => {
  return {
    root: {
      margin: "0 23px",
    },
  };
};

/**
 * Styling for the table stack
 */
export const tableStackStyles = (): IStackStyles => {
  return {
    root: {
      height: "100%",
    },
  };
};

/**
 * Styling for container wrapping inner detail contents (i.e. user's access control)
 */
export const DetailWrapper = styled.div`
  padding: 10px;
  height: 100%;
  width: 100%;
  background-color: ${theme.palette.white};
  border-radius: 0.5rem;
`;

/**
 * Styling for icon image when using custom SVG with text to right
 */
export const IconImage = styled.img`
  padding-right: 0.5rem;
  margin-bottom: 4px;
`;

/**
 * Styling for separator between panels (i.e. user's access control)
 */
export const Divider = styled.div`
  height: 36px;
`;

/**
 * Styling for return link
 */
export const ReturnLink = styled(Link)`
  color: ${theme.palette.black};
  padding-bottom: 1rem;
  font-size: ${FontSizes.mediumPlus};
`;

/**
 * Flex column styles.
 */
export const RDFlexRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
`;

/**
 * Styling for the row that holds the title, results count and download button.
 */
export const RDPaddedFlexRow = styled(RDFlexRow)`
  padding-left: 23px;
  padding-right: 23px;
  padding-top: 23px;
`;

/**
 * Styling download button.
 */
export const RDButtonStyles: IButtonStyles = {
  root: {
    marginLeft: "1.45rem",
  },
};

/**
 * Styling for text.
 */
export const RDTextStyles: ITextStyles = {
  root: {
    fontSize: FontSizes.size20,
    fontWeight: FontWeights.semibold,
    color: theme.palette.black,
    flexGrow: 2,
  },
};
